<template>
  <div v-if="!isImageValid && title" :class="imagelessClass">
    {{ title.slice(0, 2) }}
  </div>
  <div v-else-if="!isImageValid" :class="imagelessAndTitlelessClass">
    <img :alt="alt || title" src="./images/missing-avatar.svg" :width="requiredSize" :height="requiredSize" />
  </div>
  <div v-else :class="imagefullClass">
    <NuxtImg
      :title="title"
      :src="src"
      :alt="alt || title"
      :width="requiredSize"
      :height="requiredSize"
      class="avatar__img"
      preset="avatar"
      :loading="loading"
      @error="onError"
    />
    <div class="avatar__img-ring"></div>
  </div>
</template>

<script>
const typeMapping = {
  big: 124,
  'semi-big': 112,
  large: 84,
  medium: 48,
  'semi-medium': 40,
  small: 32,
  'extra-small': 24,
  tiny: 21,

  'next-big': 104,
  'next-medium-xl': 54,
  'next-medium': 40,
  'next-small': 30,
  'next-tiny': 20,
  'next-medium-tiny': 18,
  'next-extra-tiny': 16,
};

export default {
  name: 'Avatar',
  props: {
    src: {
      type: String,
      default: null,
    },
    id: {
      type: [String, Number],
      default: null,
    },
    alt: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    href: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    // Size in pixels, overrides type
    size: {
      type: Number,
    },
    target: {
      type: String,
      default: '_self',
    },
    clickable: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: String,
      default: null,
      validator: value => !!value.match(/^(lazy|eager)$/),
    },
    borderless: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hasError: false,
    };
  },
  computed: {
    colorId() {
      return !this.id ? 'default' : this.id % 5;
    },
    requiredSize() {
      return this.size || typeMapping[this.type];
    },
    srcPresent() {
      return !!this.src && !this.src.match('/missing.png');
    },
    baseClassList() {
      return {
        avatar: true,
        [`avatar--${this.type}`]: true,
      };
    },
    imagelessClass() {
      return {
        ...this.baseClassList,
        [`avatar--color-${this.colorId}`]: true,
      };
    },
    imagelessAndTitlelessClass() {
      return {
        ...this.baseClassList,
        'avatar--missing': true,
      };
    },
    imagefullClass() {
      return {
        ...this.baseClassList,
        'avatar--non-empty': true,
        'avatar--borderless': this.borderless,
      };
    },
    isImageValid() {
      return this.srcPresent && !this.hasError;
    },
  },
  methods: {
    handleClick(event) {
      this.clickable ? this.$emit('click', event) : event.stopImmediatePropagation();
    },
    onError() {
      this.hasError = true;
    },
  },
};
</script>

<style scoped lang="scss">
$avatar-initials-color: rgba(var(--universal-color-white-rgb), 0.8);
$avatar-background-colors: var(--universal-color-brand) var(--universal-color-pineapple)
  var(--universal-color-pink-salmon) var(--universal-color-jungle) var(--universal-color-sapphire);

@mixin get-size($size) {
  width: $size;
  height: $size;
}

.avatar {
  position: relative;
  width: 64px;
  height: 64px;
  background: var(--universal-color-gray-80);
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
  color: var(--universal-color-gray-50);

  @include caption1-semibold;
  @include flex-center;

  text-decoration: none;
  text-transform: uppercase;

  &--next-big {
    @include get-size(104px);

    font-size: 33px;
    letter-spacing: -1px;
    line-height: 104px;
  }

  &--next-medium {
    @include get-size(40px);
  }

  &--next-small {
    @include get-size(30px);
  }

  &--next-tiny {
    @include get-size(20px);

    font-size: 9px;
  }

  &--next-medium-tiny {
    @include get-size(18px);

    font-size: 9px;
  }

  &--next-extra-tiny {
    @include get-size(16px);

    font-size: 9px;
  }

  &--non-empty {
    background: none;
    border: 1px solid var(--universal-color-transparent-20);
  }

  &--borderless {
    border: none;
  }

  &--big {
    @include get-size(124px);

    font-size: 24px;
    font-weight: bold;
    letter-spacing: 0.5px;
  }

  &--large {
    @include get-size(84px);

    font-size: 28px;
  }

  &--medium {
    @include get-size(48px);

    font-size: 18px;
  }

  &--semi-medium {
    @include get-size(40px);

    font-size: 14px;
    font-weight: bold;
  }

  &--small {
    @include get-size(32px);

    font-weight: bold;
    font-size: 14px;
  }

  &--extra-small {
    @include get-size(24px);

    font-size: 12px;
    font-weight: 500;
  }

  &--tiny {
    @include get-size(21px);

    font-size: 10px;
    font-weight: 500;
  }

  &--missing {
    background: none;
  }

  & > img,
  &__img {
    min-height: 100%;
    min-width: 100%;
  }

  @each $color in $avatar-background-colors {
    $i: index($avatar-background-colors, $color) - 1;

    &--color-#{$i} {
      background: $color;
      color: $avatar-initials-color;
    }
  }

  &__img-ring {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    opacity: 0.1;
    border: 1px solid var(--universal-color-gray-50);
    pointer-events: none;
    border-radius: 50%;
  }
}
</style>
